import { useState } from "react";
import KpiCard from "@/components/shared/KpiCard";
import LoadingSkeleton from "@/components/shared/LoadingSkeleton";
import ErrorState from "@/components/shared/ErrorState";
import EmptyState from "@/components/shared/EmptyState";
import { useTreeStatistics } from "@/hooks";
import type { TreeStatisticsQuery } from "@/lib/api";
import type { CountItem, Distribution } from "@/types/tree-statistics";
import { recommendationText, riskStyle } from "../../../dashboard/labels";

// Rótulos dos códigos que o servidor devolve; o código fica visível quando não houver rótulo.
const TEXT: Record<string, Record<string, string>> = {
  dap: { ATE_10: "até 10 cm", "10_20": "10 a 20 cm", "20_40": "20 a 40 cm", "40_60": "40 a 60 cm", ACIMA_60: "60 cm ou mais" },
  height: { ATE_3: "até 3 m", "3_6": "3 a 6 m", "6_10": "6 a 10 m", "10_15": "10 a 15 m", ACIMA_15: "15 m ou mais" },
  health: { F1: "F1 — Boa / vigorosa", F2: "F2 — Regular", F3: "F3 — Ruim / declínio", F4: "F4 — Morta ou morte iminente" },
  roots: {
    R1: "R1 — Subterrâneas",
    R2: "R2 — Superficiais, dentro da área",
    R3: "R3 — Superficiais, além da área",
    R4: "R4 — Com dano ou conflito",
  },
  priority: { P0: "P0 — Emergência", P1: "P1 — Urgente", P2: "P2 — Alta", P3: "P3 — Programada", P4: "P4 — Monitoramento" },
  origin: { NATIVA: "Nativa", EXOTICA: "Exótica", NAO_INFORMADA: "Origem não informada" },
  interference: {
    OVERHEAD_NETWORK: "Rede aérea",
    SIDEWALK: "Calçada",
    TRAFFIC_VISIBILITY: "Visibilidade de tráfego",
    LIGHTING: "Iluminação",
    BUILDING: "Edificação",
    UNDERGROUND_INFRASTRUCTURE: "Infraestrutura subterrânea",
    URBAN_FURNITURE: "Mobiliário urbano",
    OTHER: "Outro",
  },
  recPriority: { BAIXA: "Baixa", MEDIA: "Média", ALTA: "Alta", CRITICA: "Crítica" },
  streetType: {
    RUA: "Rua", AVENIDA: "Avenida", ALAMEDA: "Alameda", TRAVESSA: "Travessa", PRACA: "Praça", VIELA: "Viela",
    RODOVIA: "Rodovia", ESTRADA: "Estrada", VIA: "Via", MARGINAL: "Marginal", VIADUTO: "Viaduto", LARGO: "Largo",
    OUTRO: "Outro tipo",
  },
};

const pretty = (r: string) => r.charAt(0) + r.slice(1).toLowerCase();
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

function BarRow({ label, count, of, tone = "bg-primary-500" }: { label: string; count: number; of: number; tone?: string }) {
  const share = pct(count, of);
  return (
    <li className="text-xs">
      <div className="flex justify-between gap-3 mb-0.5">
        <span className="text-foreground-700 truncate" title={label}>{label}</span>
        <span className="text-foreground-500 whitespace-nowrap">
          {count.toLocaleString("pt-BR")} · {share}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-background-100 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${share}%` }} />
      </div>
    </li>
  );
}

interface CardProps {
  title: string;
  /** n do indicador e o universo em que ele foi medido. */
  n: number;
  of: number;
  emptyText: string;
  missingText?: string;
  children: React.ReactNode;
}

function StatCard({ title, n, of, emptyText, missingText = "sem avaliação", children }: CardProps) {
  return (
    <section className="bg-white border border-background-200/70 rounded-card p-4">
      <header className="flex items-baseline justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
        <span className="text-[11px] text-foreground-400 whitespace-nowrap">
          n = {n} de {of}
          {of > n && ` · ${of - n} ${missingText}`}
        </span>
      </header>
      {n === 0 ? <p className="text-xs text-foreground-400 py-2">{emptyText}</p> : <ul className="space-y-2.5">{children}</ul>}
    </section>
  );
}

function DistributionBars({ dist, labels, tone }: { dist: Distribution; labels?: Record<string, string>; tone?: (code: string) => string }) {
  return (
    <>
      {dist.items.map((it: CountItem) => (
        <BarRow key={it.code} label={labels?.[it.code] ?? it.code} count={it.count} of={dist.total} tone={tone?.(it.code)} />
      ))}
    </>
  );
}

const RISK_TONE: Record<string, string> = { BAIXO: "bg-primary-500", MEDIO: "bg-warning-400", ALTO: "bg-warning-600", CRITICO: "bg-danger-600" };
const HEALTH_TONE: Record<string, string> = { F1: "bg-primary-500", F2: "bg-warning-400", F3: "bg-danger-500", F4: "bg-danger-700" };

function Select({
  label, value, onChange, children, disabled,
}: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <label className="block min-w-0">
      <span className="text-[11px] text-foreground-500 block mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full text-sm border border-background-200 rounded-md px-3 py-2 bg-white disabled:opacity-50"
      >
        {children}
      </select>
    </label>
  );
}

const EMPTY_SCOPE: TreeStatisticsQuery = { region: null, bairroId: null, quadraId: null, streetType: null, street: null };

export default function TreeStatisticsSection() {
  const [scope, setScope] = useState<TreeStatisticsQuery>(EMPTY_SCOPE);
  const { data: stats, loading, error } = useTreeStatistics(scope);

  // Mudar um nível limpa os de baixo: a opção escolhida antes pode não existir no novo recorte.
  const setRegion = (region: string) => setScope({ ...EMPTY_SCOPE, region: region || null });
  const setBairro = (v: string) => setScope({ ...EMPTY_SCOPE, region: scope.region, bairroId: Number(v) || null });
  const setQuadra = (v: string) => setScope({ ...scope, quadraId: Number(v) || null });
  const setStreetType = (v: string) => setScope({ ...scope, streetType: v || null, street: null });
  const setStreet = (v: string) => setScope({ ...scope, street: v || null });
  const filtered = Object.values(scope).some((v) => v !== null);

  const opts = stats?.options;
  const regionLabel = (r: string) => pretty(r);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-background-200/70 rounded-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Select label="Zona / região" value={scope.region ?? ""} onChange={setRegion} disabled={!opts}>
            <option value="">Todas as regiões</option>
            {opts?.regions.map((r) => (
              <option key={r.code} value={r.code}>{regionLabel(r.code)} ({r.count})</option>
            ))}
          </Select>
          <Select label="Bairro" value={scope.bairroId ? String(scope.bairroId) : ""} onChange={setBairro} disabled={!opts}>
            <option value="">Todos os bairros</option>
            {opts?.bairros.map((b) => (
              <option key={b.bairroId} value={b.bairroId}>{b.name} ({b.count})</option>
            ))}
          </Select>
          <Select label="Quadra" value={scope.quadraId ? String(scope.quadraId) : ""} onChange={setQuadra} disabled={!opts}>
            <option value="">Todas as quadras</option>
            {opts?.quadras.map((q) => (
              <option key={q.quadraId} value={q.quadraId}>
                {q.quadraCode}{q.areaPilotoCode ? ` · ${q.areaPilotoCode}` : ""} ({q.count})
              </option>
            ))}
          </Select>
          <Select label="Tipo de via" value={scope.streetType ?? ""} onChange={setStreetType} disabled={!opts}>
            <option value="">Ruas, avenidas e demais</option>
            {opts?.streetTypes.map((t) => (
              <option key={t.code} value={t.code}>{TEXT.streetType[t.code] ?? t.code} ({t.count})</option>
            ))}
          </Select>
          <Select label="Via" value={scope.street ?? ""} onChange={setStreet} disabled={!opts}>
            <option value="">Todas as vias</option>
            {opts?.streets.map((s) => (
              <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <p className="text-[11px] text-foreground-500">
            {stats
              ? filtered
                ? `${stats.registeredTotal} de ${stats.organizationTotal} exemplares da organização neste recorte.`
                : `Total da organização: ${stats.organizationTotal} exemplares.`
              : "Carregando recorte..."}
          </p>
          {filtered && (
            <button onClick={() => setScope(EMPTY_SCOPE)} className="text-xs text-primary-600 hover:underline cursor-pointer">
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {loading && !stats && <LoadingSkeleton type="kpi-grid" count={4} />}
      {error && <ErrorState message="Não foi possível carregar as estatísticas do inventário." />}

      {stats && stats.organizationTotal === 0 && (
        <EmptyState
          icon="ri-plant-line"
          title="Nenhum exemplar cadastrado"
          description="As estatísticas aparecem conforme os exemplares são cadastrados no CTA."
        />
      )}

      {stats && stats.organizationTotal > 0 && (
        <div className={`space-y-4 ${loading ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard label="Exemplares no recorte" value={stats.registeredTotal} icon="ri-plant-line" variant="success" />
            <KpiCard
              label="Com espécie identificada"
              value={`${stats.withSpecies} (${pct(stats.withSpecies, stats.registeredTotal)}%)`}
              icon="ri-leaf-line"
            />
            <KpiCard
              label="Com vistoria validada"
              value={`${stats.validatedTotal} (${pct(stats.validatedTotal, stats.registeredTotal)}%)`}
              icon="ri-shield-check-line"
            />
            <KpiCard label="Vistoria vencida" value={stats.overdueCount} icon="ri-calendar-close-line" variant={stats.overdueCount ? "warning" : "default"} />
            <KpiCard label="Com conflito registrado" value={stats.treesWithInterference} icon="ri-flashlight-line" variant={stats.treesWithInterference ? "danger" : "default"} />
          </div>

          <p className="text-[11px] text-foreground-500">
            Dados medidos no CTA. Condição, medidas, conflitos e manejo vêm só da última vistoria <strong>validada</strong> de cada
            exemplar; quem não tem vistoria validada aparece como "sem avaliação" e não entra em nenhuma classe. Cada quadro indica
            o seu n. Região e bairro vêm da malha oficial de Goiânia; a quadra é a mais próxima a até {stats.maxDistanceM} m.
          </p>

          {stats.registeredTotal === 0 ? (
            <p className="text-sm text-foreground-500 py-6 text-center">Nenhum exemplar neste recorte.</p>
          ) : (
            <>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 pt-1">Composição</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <StatCard title="Espécies mais frequentes" n={stats.withSpecies} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar com espécie identificada." missingText="sem espécie">
                  {stats.species.slice(0, 10).map((s) => (
                    <BarRow
                      key={s.speciesId}
                      label={`${s.popularName ?? "—"}${s.scientificName ? ` (${s.scientificName})` : ""}`}
                      count={s.count}
                      of={stats.withSpecies}
                      tone={stats.concentration.overThreshold.includes(s.speciesId) ? "bg-warning-500" : "bg-primary-500"}
                    />
                  ))}
                  <li className="text-[11px] text-foreground-500 pt-1">
                    {stats.concentration.sampleSufficient
                      ? stats.concentration.overThreshold.length
                        ? `${stats.concentration.overThreshold.length} espécie(s) acima de ${Math.round(stats.concentration.thresholdShare * 100)}% do inventário (referência 10-20-30): diversificar no plantio.`
                        : `Nenhuma espécie acima de ${Math.round(stats.concentration.thresholdShare * 100)}% do inventário.`
                      : `Concentração por espécie só é avaliada com ${stats.concentration.minSample} ou mais exemplares identificados.`}
                    {stats.species.length > 10 && ` ${stats.species.length - 10} outras espécies não listadas.`}
                  </li>
                </StatCard>
                <StatCard title="Origem da espécie" n={stats.origin.total} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar com espécie identificada." missingText="sem espécie">
                  <DistributionBars dist={stats.origin} labels={TEXT.origin} />
                </StatCard>
                <StatCard title="Grupos de atenção" n={stats.withSpecies} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar com espécie identificada." missingText="sem espécie">
                  <BarRow label="Frutíferas" count={stats.fruitCount} of={stats.withSpecies} />
                  <BarRow label="Espécies invasoras" count={stats.invasiveCount} of={stats.withSpecies} tone="bg-danger-500" />
                  <li className="text-[11px] text-foreground-500 pt-1">Classificação do catálogo de espécies.</li>
                </StatCard>
              </div>

              <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 pt-1">Estrutura e condição</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <StatCard title="Classes de DAP" n={stats.dapClasses.total} of={stats.registeredTotal}
                  emptyText="Nenhum DAP medido em vistoria validada.">
                  <DistributionBars dist={stats.dapClasses} labels={TEXT.dap} />
                </StatCard>
                <StatCard title="Classes de altura" n={stats.heightClasses.total} of={stats.registeredTotal}
                  emptyText="Nenhuma altura medida em vistoria validada.">
                  <DistributionBars dist={stats.heightClasses} labels={TEXT.height} />
                </StatCard>
                <StatCard title="Condição fitossanitária (F)" n={stats.health.total} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada com condição avaliada.">
                  <DistributionBars dist={stats.health} labels={TEXT.health} tone={(c) => HEALTH_TONE[c] ?? "bg-primary-500"} />
                </StatCard>
                <StatCard title="Sistema radicular (R)" n={stats.roots.total} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada com raiz avaliada.">
                  <DistributionBars dist={stats.roots} labels={TEXT.roots} />
                </StatCard>
                <StatCard title="Classe de risco" n={stats.risk.total} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada com risco avaliado.">
                  <DistributionBars dist={stats.risk} labels={Object.fromEntries(stats.risk.items.map((i) => [i.code, riskStyle(i.code).text]))}
                    tone={(c) => RISK_TONE[c] ?? "bg-primary-500"} />
                </StatCard>
                <StatCard title="Prioridade de atendimento (P)" n={stats.priority.total} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada com prioridade definida.">
                  <DistributionBars dist={stats.priority} labels={TEXT.priority} />
                </StatCard>
              </div>

              <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 pt-1">Conflitos e manejo</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <StatCard title="Conflitos com a infraestrutura" n={stats.validatedTotal} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada.">
                  <BarRow label="Exemplares com algum conflito" count={stats.treesWithInterference} of={stats.validatedTotal} tone="bg-danger-500" />
                  {stats.interferences.items.map((it) => (
                    <BarRow key={it.code} label={TEXT.interference[it.code] ?? it.code} count={it.count} of={stats.validatedTotal} tone="bg-warning-500" />
                  ))}
                </StatCard>
                <StatCard title="Recomendações em aberto" n={stats.validatedTotal} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada.">
                  <BarRow label="Exemplares com manejo pendente" count={stats.treesWithOpenRecommendation} of={stats.validatedTotal} />
                  {stats.recommendationTypes.items.map((it) => (
                    <BarRow key={it.code} label={recommendationText(it.code)} count={it.count} of={stats.recommendationTypes.total} tone="bg-accent-500" />
                  ))}
                </StatCard>
                <StatCard title="Revistoria" n={stats.withNextInspection} of={stats.registeredTotal}
                  emptyText="Nenhuma vistoria validada com data de revistoria." missingText="sem data prevista">
                  <BarRow label="Vencidas" count={stats.overdueCount} of={stats.withNextInspection} tone="bg-danger-500" />
                  <BarRow label="No prazo" count={stats.withNextInspection - stats.overdueCount} of={stats.withNextInspection} />
                  {stats.recommendationPriorities.items.map((it) => (
                    <BarRow key={it.code} label={`Manejo de prioridade ${TEXT.recPriority[it.code] ?? it.code}`} count={it.count}
                      of={stats.recommendationPriorities.total} tone="bg-accent-500" />
                  ))}
                </StatCard>
              </div>

              <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground-500 pt-1">Distribuição territorial</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <StatCard title="Por zona / região" n={stats.byRegion.total} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar dentro da malha de bairros oficial." missingText="fora da malha oficial">
                  <DistributionBars dist={stats.byRegion}
                    labels={Object.fromEntries(stats.byRegion.items.map((i) => [i.code, regionLabel(i.code)]))} />
                </StatCard>
                <StatCard title="Por bairro" n={stats.byNeighborhood.total} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar dentro da malha de bairros oficial." missingText="fora da malha oficial">
                  <DistributionBars dist={{ ...stats.byNeighborhood, items: stats.byNeighborhood.items.slice(0, 10) }} />
                </StatCard>
                <StatCard title="Por quadra" n={stats.byQuadra.reduce((a, q) => a + q.count, 0)} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar a até 20 m de uma quadra operacional." missingText="sem quadra operacional">
                  {stats.byQuadra.slice(0, 10).map((q) => (
                    <BarRow key={q.quadraId} label={`${q.quadraCode}${q.areaPilotoCode ? ` · ${q.areaPilotoCode}` : ""}`}
                      count={q.count} of={stats.registeredTotal} />
                  ))}
                </StatCard>
                <StatCard title="Por tipo de via" n={stats.byStreetType.total} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar com logradouro registrado." missingText="sem logradouro">
                  <DistributionBars dist={stats.byStreetType} labels={TEXT.streetType} />
                </StatCard>
                <StatCard title="Por via" n={stats.byStreet.reduce((a, s) => a + s.count, 0)} of={stats.registeredTotal}
                  emptyText="Nenhum exemplar com logradouro registrado." missingText="sem logradouro">
                  {stats.byStreet.slice(0, 10).map((s) => (
                    <BarRow key={s.name} label={s.name} count={s.count} of={stats.registeredTotal} />
                  ))}
                </StatCard>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
